import { Form, Head } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import OnboardingPasswordRules from "@/components/onboarding/onboarding-password-rules"
import TextLink from "@/components/text-link"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AuthLayout from "@/layouts/auth-layout"
import { signInPath, signUpPath } from "@/routes"

/**
 * OKS-306 — Server payload shape; must match
 * `User.password_requirements_payload`.
 */
export interface RegisterProps {
  password_requirements: {
    min_length: number
    rule_keys: string[]
  }
}

/**
 * Legacy non-invite signup page (auth-zero scaffold). Gated by
 * `ensure_not_production_or_invite_path` on the controller — so this
 * surface is dev/staging only. OKS-306 reuses
 * `<OnboardingPasswordRules>` so the rule-hint pattern is consistent
 * with the other three password-set surfaces.
 */
export default function Register({ password_requirements }: RegisterProps) {
  const { t } = useTranslation()
  const passwordRulesId = "register-password-rules"

  return (
    <AuthLayout
      title={t("registrations_new.title")}
      description={t("registrations_new.description")}
    >
      <Head title={t("registrations_new.title")} />
      <Form
        method="post"
        action={signUpPath()}
        resetOnSuccess={["password", "password_confirmation"]}
        disableWhileProcessing
        className="flex flex-col gap-6"
      >
        {({ processing, errors }) => (
          <>
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="name">
                  {t("registrations_new.name_label")}
                  {/* OKS-728 — required marker; native `required` dropped for
                      server-side translated inline errors (no native bubble). */}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  name="name"
                  aria-required="true"
                  aria-invalid={errors.name ? "true" : undefined}
                  autoFocus
                  tabIndex={1}
                  autoComplete="name"
                  disabled={processing}
                  placeholder={t("registrations_new.name_placeholder")}
                />
                <InputError messages={errors.name} className="mt-2" />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="email">
                  {t("registrations_new.email_label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  aria-required="true"
                  aria-invalid={errors.email ? "true" : undefined}
                  tabIndex={2}
                  autoComplete="email"
                  placeholder={t("registrations_new.email_placeholder")}
                />
                <InputError messages={errors.email} />
              </div>

              <OnboardingPasswordRules
                id={passwordRulesId}
                minLength={password_requirements.min_length}
                ruleKeys={password_requirements.rule_keys}
              />

              <div className="grid gap-2">
                <Label htmlFor="password">
                  {t("registrations_new.password_label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Input
                  id="password"
                  type="password"
                  name="password"
                  aria-required="true"
                  aria-invalid={errors.password ? "true" : undefined}
                  tabIndex={3}
                  autoComplete="new-password"
                  placeholder={t("registrations_new.password_placeholder")}
                  aria-describedby={[
                    passwordRulesId,
                    errors.password ? "register-error-password" : null,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
                <InputError
                  id="register-error-password"
                  messages={errors.password}
                />
              </div>

              <div className="grid gap-2">
                <Label htmlFor="password_confirmation">
                  {t("registrations_new.password_confirmation_label")}
                  <span className="text-destructive ml-0.5" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Input
                  id="password_confirmation"
                  type="password"
                  name="password_confirmation"
                  aria-required="true"
                  aria-invalid={
                    errors.password_confirmation ? "true" : undefined
                  }
                  tabIndex={4}
                  autoComplete="new-password"
                  placeholder={t(
                    "registrations_new.password_confirmation_placeholder",
                  )}
                />
                <InputError messages={errors.password_confirmation} />
              </div>

              <Button type="submit" className="mt-2 w-full" tabIndex={5}>
                {processing && <Spinner />}
                {t("registrations_new.submit")}
              </Button>
            </div>

            <div className="text-muted-foreground text-center text-sm">
              {t("registrations_new.signin_prompt")}{" "}
              <TextLink href={signInPath()} tabIndex={6}>
                {t("registrations_new.signin_link")}
              </TextLink>
            </div>
          </>
        )}
      </Form>
    </AuthLayout>
  )
}
